import { useState } from "react";
import usePageState from "../../../hooks/usePageState";
import { ToppingModel } from "../../../../infrastructure/model/ProductItemModel";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import ActionHeader from "../../../components/ActionHeader";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useModal from "../../../hooks/useModal";
import BaseTable from "../../../components/BaseTable";
import useConfirmAction from "../../../hooks/useConfirmAction";
import { useToppingContext } from "../../../context/AppToppingContext";
import InsertToppingDialog from "./InsertToppingDialog";
import { currencyFormat } from "../../../../applications/utils/NumberUtils";
import {
  deleteToppingUseCase,
  getAllToppingUseCase,
} from "../../../../applications/useCase/ApiUseCase/ItemApiUseCase";

function Toppings() {
  const { toppings, setToppings } = useToppingContext();
  const { isLoading, setLoading, showError, showSuccess } = usePageState();
  const [createModal, openCreateModal] = useModal();
  const [selectedItem, setSelectedItem] = useState<ToppingModel>();

  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm(params) {
      handleDeleteItem();
    },
  });

  async function getAllToppings() {
    const res = await getAllToppingUseCase({
      setLoading,
      showError,
    });
    setToppings(res);
  }

  async function handleDeleteItem() {
    if (!selectedItem?.id) return;

    const res = await deleteToppingUseCase({
      toppingId: selectedItem.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setToppings(toppings.filter((e) => e.id !== selectedItem.id));
      setSelectedItem(undefined);
    }
  }

  async function handleOpenCreateDialog(updateItem?: ToppingModel) {
    const result = await openCreateModal<ToppingModel>((close) => {
      return <InsertToppingDialog onClose={close} updateItem={updateItem} />;
    });
    if (result && result.id) {
      if (updateItem) {
        const indexUpdate = toppings.findIndex((e) => e.id === updateItem.id);
        setToppings([
          ...toppings.slice(0, indexUpdate),
          result,
          ...toppings.slice(indexUpdate + 1),
        ]);
      } else {
        setToppings([result, ...toppings]);
      }
    }
  }

  useAsyncEffect(async () => {
    await getAllToppings();
  }, []);

  return (
    <div className="h-100 flex flex-column">
      <ActionHeader
        actions={[
          {
            icon: <ReloadOutlined />,
            isNormal: true,
            userClick: getAllToppings,
            type: "primary",
          },
          {
            icon: <PlusCircleOutlined />,
            isNormal: true,
            userClick: handleOpenCreateDialog,
            type: "primary",
          },
          {
            icon: <EditOutlined />,
            isNormal: true,
            userClick: () => handleOpenCreateDialog(selectedItem),
            disabled: !selectedItem?.id,
            type: "primary",
          },
          {
            isNormal: true,
            userClick: confirmDelete,
            icon: <DeleteOutlined />,
            disabled: !selectedItem?.id,
            color: "danger",
            variant: "outlined",
          },
        ]}
      />
      <div className="flex-1">
        <BaseTable
          dataSource={toppings}
          loading={isLoading}
          columns={[
            {
              title: "ID",
              dataIndex: "id",
              key: "id",
              width: 100,
            },
            {
              title: "Tên",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Giá",
              dataIndex: "price",
              key: "price",
              render(value, record, index) {
                return currencyFormat(value);
              },
            },
            {
              title: "Mở Sale",
              dataIndex: "can_sale",
              key: "can_sale",
              render(value, record, index) {
                return value ? "Có" : "Không";
              },
            },
          ]}
          rowKey={"id"}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedItem?.id ? [selectedItem.id] : [],
            onSelect(record) {
              setSelectedItem(record);
            },
          }}
          pagination={{
            pageSize: 30,
          }}
        />
      </div>

      {createModal}
      {confirmDeleteDialog}
    </div>
  );
}

export default Toppings;
